import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Alert 
} from '@mui/material'; 
import { io } from 'socket.io-client';   
import LoginForm from './components/LoginForm';
import LoadingScreen from './components/LoadingScreen';
import JobsList from './components/JobsList'; 
import ConfigurationForm from './components/ConfigurationForm';  
import AutomationStatus from './components/AutomationStatus'; 

const socket = io(process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003');

function App() {
  // Initialize formData with URL parameters if available
  const [formData, setFormData] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      email: urlParams.get('email') || '',
      password: ''
    };
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [jobs, setJobs] = useState([]);
  const [currentStep, setCurrentStep] = useState('login'); // 'login', 'jobs', 'configure', 'automation', 'reauth'
  
  // Initialize sessionId from URL parameters if available
  const [sessionId, setSessionId] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('session') || null;
  });
  
  const [selectedJob, setSelectedJob] = useState(null);
  const [autoReplyConfig, setAutoReplyConfig] = useState({
    firstMessage: '',
    calendlyLink: '',
    noCvMessage: "Could you please share your CV? I'd like to review your experience and discuss potential opportunities."
  });
  const [requiresReauth, setRequiresReauth] = useState(false);
  const [candidateAnalyses, setCandidateAnalyses] = useState([]);
  const [automationStats, setAutomationStats] = useState({
    currentMessage: 0,
    totalMessages: 0,
    interviewed: 0,
    rejected: 0,
    questioned: 0,
    skipped: 0,
    requiresHuman: 0,
    errors: 0
  });
  const [isAutomationRunning, setIsAutomationRunning] = useState(false);

  // Add useEffect to handle URL parameter updates
  useEffect(() => {
    const updateUrlParams = () => {
      const urlParams = new URLSearchParams(window.location.search);
      let hasChanges = false;
      
      // Add or update session parameter
      if (sessionId && sessionId !== urlParams.get('session')) {
        urlParams.set('session', sessionId);
        hasChanges = true;
      }
      
      // Add or update email parameter if it exists
      if (formData.email && formData.email !== urlParams.get('email')) {
        urlParams.set('email', formData.email);
        hasChanges = true;
      }
      
      // Update URL without causing page reload
      if (hasChanges) {
        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState({}, '', newUrl);
      }
    };
    
    updateUrlParams();
  }, [sessionId, formData.email]);

  useEffect(() => {
    let cleanupJobs = null;
    let cleanupError = null;

    // Handle socket connection
    const handleConnect = () => {
      console.log('Socket connected:', socket.id);
      setSessionId(socket.id);

      // Set up event listeners after connection is established
      const handleJobs = (data) => {
        console.log('Received jobs:', data);
        setJobs(data.jobs || []);
        setCurrentStep('jobs');
        setSuccess('Jobs loaded successfully! Select a job to add automation.');
      };

      const handleError = (data) => {
        console.error('Socket error:', data); 
        setError(data.error || 'An error occurred');
      };

      const handleSessionStatus = (data) => {  
        console.log('Session status:', data);
        if (data.status === 'running') {
          setSuccess('Successfully logged in! Fetching your job posts...');
        } else if (data.status === 'jobs_ready') {
          setSuccess(data.message || 'Jobs fetching completed!');
        } else if (data.status === 'error') {
          setError(data.error || 'An error occurred during login');
          setCurrentStep('login');
        }
      };

      // Set up event listeners for this specific session
      const jobsEventName = `session:${socket.id}:jobs`;
      const errorEventName = `session:${socket.id}:error`;
      const statusEventName = `session:${socket.id}:sessionStatus`;
      
      console.log('Setting up listeners for:', jobsEventName, errorEventName, statusEventName);
      
      socket.on(jobsEventName, handleJobs);
      socket.on(errorEventName, handleError);
      socket.on(statusEventName, handleSessionStatus);

      // Store cleanup functions
      cleanupJobs = () => {
        socket.off(jobsEventName, handleJobs);
        socket.off(statusEventName, handleSessionStatus);
      };
      cleanupError = () => socket.off(errorEventName, handleError);
    };

    // Listen for socket connection
    socket.on('connect', handleConnect);

    // If already connected, set up listeners immediately
    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off('connect', handleConnect);
      if (cleanupJobs) cleanupJobs();
      if (cleanupError) cleanupError();
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('Logging in and fetching jobs...');
    setCurrentStep('loading');

    try {
      // Emit login event to backend
      socket.emit('startSession', {
        user: {
          email: formData.email,
          password: formData.password
        }
      });

    } catch (err) {
      setError('Failed to connect to the server');
      setCurrentStep('login');
    }
  };

  const handleJobSelect = (job) => {
    console.log('Selected job for automation:', job);
    setSelectedJob(job);
    setCurrentStep('configure');
    setSuccess(`Configure auto-reply for: ${job.title}`);
  };

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setAutoReplyConfig(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleStartAutomation = (e) => {
    e.preventDefault();
    
    if (!autoReplyConfig.firstMessage.trim()) {
      setError('Please enter a successful message');
      return;
    }
    
    if (!autoReplyConfig.calendlyLink.trim()) {
      setError('Please enter a Calendly link');
      return;
    }

    // Emit job selection with configuration to backend
    socket.emit('selectJobForAutomation', {
      jobId: selectedJob.id,
      jobTitle: selectedJob.title,
      jobLink: selectedJob.link,
      firstMessage: autoReplyConfig.firstMessage,
      calendlyLink: autoReplyConfig.calendlyLink,
      noCvMessage: autoReplyConfig.noCvMessage,
      messagesUrl: selectedJob.messagesUrl
    });

    setCurrentStep('automation');
    setSuccess(`Automation started for: ${selectedJob.title}`);
    setError('');

    // Listen for automation status
    socket.on('automationStatus', (data) => {
      if (data.status === 'started') {
        setSuccess(`Automation started for job: ${data.jobId}`);
        setIsAutomationRunning(true);
        // Reset analyses when starting new automation
        setCandidateAnalyses([]);
        setAutomationStats({
          currentMessage: 0,
          totalMessages: data.unreadMessages?.length || 0,
          interviewed: 0,
          rejected: 0,
          questioned: 0,
          skipped: 0,
          requiresHuman: 0,
          errors: 0
        });
      } else if (data.status === 'stopped') {
        setSuccess(data.message || 'Automation stopped successfully');
        setIsAutomationRunning(false);
        // Keep the automation status screen visible to show statistics
        // Don't reset states - user can see what was accomplished
        // Don't change currentStep - stay on automation screen to show results
      } else if (data.status === 'authentication_required') {
        setError(data.error || 'Authentication required');
        setRequiresReauth(true);
        setCurrentStep('reauth');
      } else if (data.status === 'navigation_failed') {
        setError(`Navigation failed: ${data.error}`);
        // Could offer retry option here
      } else if (data.status === 'error') {
        setError(data.error || 'Automation failed');
      }
    });

    // Listen for OpenAI candidate analysis
    socket.on('candidateAnalysis', (data) => {
      console.log('Candidate analysis received:', data);
      
      // Add the analysis to our list
      setCandidateAnalyses(prev => [...prev, {
        id: data.personData?.candidateId || `${Date.now()}-${data.currentMessage}`,
        timestamp: data.timestamp || new Date().toISOString(),
        person: data.personData,
        recommendation: data.recommendationData,
        status: data.status,
        actionResult: data.actionResult,
        conversationHistory: data.conversationHistory,
        error: data.error,
        message: data.message
      }]);

      // Update automation stats
      setAutomationStats(prev => ({
        ...prev,
        currentMessage: data.currentMessage || prev.currentMessage,
        totalMessages: data.totalMessages || prev.totalMessages
      }));
    });

    // Listen for specific OpenAI recommendations (real-time updates)
    socket.on('openaiRecommendation', (data) => {
      console.log('OpenAI recommendation received:', data);
      
      // Update stats based on action taken
      if (data.actionTaken?.success) {
        setAutomationStats(prev => {
          const newStats = { ...prev };
          
          switch (data.actionTaken.type) {
            case 'INTERVIEW':
              newStats.interviewed++;
              break;
            case 'REJECT':
              newStats.rejected++;
              break;
            case 'ASK_QUESTIONS':
              newStats.questioned++;
              break;
            case 'SKIPPING':
              newStats.skipped++;
              break;
            case 'REQUIRES_HUMAN':
              newStats.requiresHuman++;
              break;
            default:
              break;
          }
          
          return newStats;
        });
      }
    });

    // Listen for message processing updates
    socket.on('messageProcessing', (data) => {
      if (data.status === 'completed') {
        setSuccess(`Automation completed! Processed ${data.processedMessages}/${data.totalMessages} messages in ${data.duration}`);
        setIsAutomationRunning(false);
        setAutomationStats(prev => ({
          ...prev,
          ...data.automationStats
        }));
      } else if (data.status === 'error') {
        setAutomationStats(prev => ({
          ...prev,
          errors: prev.errors + 1
        }));
      }
    });
  };

  const renderLoginForm = () => (
    <LoginForm 
      formData={formData}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
    />
  );

  const renderJobsList = () => (
    <JobsList 
      jobs={jobs}
      handleJobSelect={handleJobSelect}
    />
  );

  const renderConfigurationForm = () => (
    <ConfigurationForm 
      selectedJob={selectedJob}
      autoReplyConfig={autoReplyConfig}
      handleConfigChange={handleConfigChange}
      handleStartAutomation={handleStartAutomation}
      setCurrentStep={setCurrentStep}
    />
  );

  const handleStopAutomation = () => {
    socket.emit('stopAutomation');
    setSuccess('Stopping automation...');
    // Note: The 'automationStatus' event is already being listened to in handleStartAutomation
    // and will handle the 'stopped' status to update the UI accordingly
  };

  const handleReauthentication = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('Re-authenticating...');

    // Emit re-authentication request
    socket.emit('reauthenticate', {
      email: formData.email,
      password: formData.password
    });

    // Listen for re-authentication status
    socket.on('reauthenticationStatus', (data) => {
      if (data.status === 'success') {
        setSuccess(data.message || 'Re-authentication successful');
        setRequiresReauth(false);
        // Try to restart automation
        if (selectedJob) {
          setTimeout(() => {
            handleStartAutomation(e);
          }, 1000);
        } else {
          setCurrentStep('jobs');
        }
      } else if (data.status === 'error') {
        setError(data.error || 'Re-authentication failed');
        setCurrentStep('login');
      }
    });
  };

  const renderAutomationStatus = () => (
    <AutomationStatus 
      selectedJob={selectedJob}
      autoReplyConfig={autoReplyConfig}
      setCurrentStep={setCurrentStep}
      handleStopAutomation={handleStopAutomation}
      candidateAnalyses={candidateAnalyses}
      automationStats={automationStats}
      isAutomationRunning={isAutomationRunning}
    />
  );

  const renderReauthForm = () => (
    <Box
      component="form"
      onSubmit={handleReauthentication}
      sx={{ mt: 1, width: '100%' }}
    >
      <Alert severity="warning" sx={{ mb: 2 }}>
        Your session has expired. Please re-enter your credentials to continue.
      </Alert>
      <LoginForm 
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleReauthentication}
        submitText="Re-authenticate"
      />
    </Box>
  );

  // Handle automation step differently - full width without container constraints
  if (currentStep === 'automation') {
    return (
      <Box sx={{ width: '100%', minHeight: '100vh' }}>
        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2, mx: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ width: '100%', mb: 2, mx: 2 }}>
            {success}
          </Alert>
        )}
        
        {renderAutomationStatus()}
      </Box>
    );
  }

  return (
    <Container component="main" maxWidth={['jobs', 'configure'].includes(currentStep) ? 'md' : 'xs'}>
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {success}
          </Alert>
        )}

        {currentStep === 'login' && renderLoginForm()}
        {currentStep === 'loading' && <LoadingScreen />}
        {currentStep === 'jobs' && renderJobsList()}
        {currentStep === 'configure' && renderConfigurationForm()}
        {currentStep === 'reauth' && renderReauthForm()}
      </Box>
    </Container>
  );
}

export default App; 